// **خريطةُ التنقّل** — الشاشات مقسومةً على أبوابٍ يفهمها من يشتغل في المكتب لا من كتب الكود.
//
// التبويبات في `App` قائمةٌ واحدة مرتّبة بالأهمّية، ومبنيّةٌ بحسب دور صاحب الجلسة. هذا
// الملف لا يضيف إليها ولا يحذف منها شيئًا — **يقسمها أبوابًا وحسب**. فما تراه في الشريط
// الجانبي هو ما يملكه المستخدم بالضبط، لا أقلّ ولا أكثر، والصلاحيات تبقى حيث هي.
//
// **ولا شاشةَ تسقط**: ما لا اسمَ لبابه هنا يقع في «غير ذلك» فيُرى ويُفتح — فمن يضيف
// شاشةً غدًا ولا يمرّ على هذا الملف لا يفقدها، يجدها في آخر القائمة فيعرف أن يصنّفها.
//
// والترتيب داخل الباب هو ترتيبُها في `App` نفسه: قرارُ الأهمّية اتُّخذ هناك مرّةً، ولا
// يُعاد اتّخاذه هنا.

export interface NavSection {
  key: string;
  /** اسم الباب كما يُقرأ فوق بنوده */
  label: string;
  /**
   * رمزُ الباب — **معرّفُ وجهةٍ** لا رسمٌ جديد: يُؤخذ من طقم رموز الوجهات نفسه
   * (navIcons)، فلا يُصان طقمان ولا يختلف خطُّ الرسم بين عنوانٍ وبنده.
   */
  icon: string;
}

/**
 * الأبواب بترتيب ظهورها. والترتيب قصدٌ لا صدفة: **ما يُفتح كل يوم أوّلًا**، ثم ما
 * يُقرأ أسبوعيًّا، ثم ما يُفتح عند الحاجة، وآخرها ما يُضبط مرّةً ويُنسى.
 */
export const SECTIONS: readonly NavSection[] = [
  // بلا عنوان: أوّلُ ما يُفتح — الصفحة الرئيسية ومعها ما يُفتح كل يوم بلا مقدّمة
  { key: 'home', label: '', icon: 'home' },
  // بلا عنوان (قرار المالك 22/09/2026: «شيل جملة الشغل اليومي»): هذه الشاشاتُ تُفتح
  // كل يوم بلا أن يُقال لصاحبها ما هي، وعنوانٌ فوقها سطرٌ يُقرأ مرّةً ثم يُتجاوَز.
  // وفيها التحصيلات كذلك (قرار المالك: «بعد الصفحة الرئيسية… وبعدها التحصيلات»).
  { key: 'daily', label: '', icon: 'crm' },
  { key: 'reports', label: 'التقارير والتحليل', icon: 'admin-dashboard' },
  { key: 'money', label: 'المال', icon: 'treasury' },
  // «الحضور والتقييم» (قرار المالك 23/09/2026): كان «الناس والحضور» — وفيه ترتيبُ البياع
  // وتقييمُه مع حضوره، فالاسم يقول ما فيه.
  { key: 'people', label: 'الحضور والتقييم', icon: 'admin-employees' },
  { key: 'tools', label: 'أدوات ومراجع', icon: 'pricing' },
  { key: 'system', label: 'الحساب والنظام', icon: 'account' },
  { key: 'other', label: 'غير ذلك', icon: 'my-notes' },
];

/** بابُ كل شاشة. وما ليس هنا يقع في «غير ذلك» — يُرى ولا يضيع. */
const OF: Record<string, string> = {
  home: 'home',

  crm: 'daily', chat: 'daily', 'team-chat': 'daily', bookings: 'daily', upcoming: 'daily',
  // «إقرار مراجعة الطيران» زرٌّ رئيسي تحت «الحجوزات» (قرار المالك 01/10/2026: «أنا عاوز زر رئيسي») — موضعه من ترتيب App
  declarations: 'daily',
  // و«حجز فندق مبدئي» تحته مباشرةً (قرار المالك 03/10/2026: «خلى الحجز المبدئى تحت اقرار مراجعة الطيران ف الزراير الرئيسية»)
  'hotel-voucher': 'daily',

  'admin-dashboard': 'reports', 'admin-analytics': 'reports', 'admin-crm': 'reports',
  // «التحصيلات» خارج البابِ المطويّ، بعد شاشات اليوم (قرار المالك 22/09/2026): شاشةٌ
  // تُفتح كل يوم لا تقريرٌ يُراجَع بين حين وحين. وموضعُها داخل الباب يتبع ترتيب
  // التبويبات في App — فتأتي بعد «هيسافروا قريب».
  'admin-payments': 'daily',
  // «دليلك في السياحة» في باب الشغل اليومي لا الأدوات (قرار المالك 23/09/2026): تحت
  // «التحصيلات» للأدمن وتحت «هيسافروا قريب» للموظفين — الموضع من ترتيب App.
  guide: 'daily',
  // «أكواد المطارات» تحت الدليل مباشرةً (قرار المالك 26/09/2026: «خليها تحت دليلك ف السياحة فى التابات الرئيسية»)
  airports: 'daily',

  'admin-settlements': 'money', treasury: 'money', commission: 'money',
  // «تقييم Google Map» في «المال» مع خطة العمولات ومرتبي (قرار المالك 23/09/2026: «طلع
  // تقييم Google Map خليه عندهم مع drop list المال»): مكافأةُ التقييم مالُ البياع لا حضوره.
  'google-rating': 'money',
  'my-payslip': 'money', 'my-partner-account': 'money',

  'admin-attendance': 'people', 'admin-employees': 'people', attendance: 'people',
  'my-rank': 'people', 'evaluate-me': 'people',

  quote: 'tools', pricing: 'tools', installment: 'tools', tafqeet: 'tools', hijri: 'tools',
  clock: 'tools', fx: 'tools', 'my-notes': 'tools',

  account: 'system', 'account-password': 'system', 'account-lock': 'system',
  'admin-audit': 'system', 'admin-backups': 'system',
};

export const sectionOf = (id: string): string => OF[id] ?? 'other';

export interface NavItem { id: string; label: string; badge?: number }

/**
 * تقسيمُ تبويبات المستخدم على الأبواب، بترتيب الأبواب وترتيبِ التبويبات داخلها.
 * والبابُ الخالي لا يُرجَع أصلًا — فلا عنوانَ فوق فراغ.
 */
export function groupTabs<T extends NavItem>(tabs: readonly T[]): { section: NavSection; items: T[] }[] {
  const out: { section: NavSection; items: T[] }[] = [];
  for (const section of SECTIONS) {
    const items = tabs.filter(t => sectionOf(t.id) === section.key);
    if (items.length > 0) out.push({ section, items });
  }
  return out;
}
